import type {BusinessPoolDepositDuplicateInfo} from '@afriex/core/domain/types';
import {formatPoolDepositDuplicateSender} from '@afriex/core/utils';

import {AfriexErrorCode} from '@/types';
import {getResponseErrorCode, getResponseErrorData} from '@/utils/errorMessage';

/**
 * Owned by `@afriex/core` (see BusinessPoolDepositDuplicateInfo): the server
 * produces it, and business-web reads the same payload, so the shape is not
 * admin's to redeclare. Aliased locally under the name this screen already
 * uses.
 */
export type PoolDepositDuplicateWarning = BusinessPoolDepositDuplicateInfo;

/**
 * Reads an overridable duplicate out of a rejected pool-deposit request, or
 * undefined if the error is anything else.
 *
 * The `existingTransactionId` test is load-bearing, not defensive: the server
 * raises DUPLICATE_REQUEST both for a durable duplicate (overridable, carries
 * publicData) and for the in-flight latch (not overridable, carries none).
 * Offering "confirm anyway" on the latter would just re-hit the same 409, so
 * only a payload with an existing transaction counts. Shared by the confirm
 * and approve hooks so that rule has one definition.
 */
export const getPoolDepositDuplicateWarning = (
  error: unknown,
): PoolDepositDuplicateWarning | undefined => {
  const data = getResponseErrorData(error) as
    PoolDepositDuplicateWarning | undefined;
  return getResponseErrorCode(error) === AfriexErrorCode.DuplicateRequest &&
    data?.existingTransactionId
    ? data
    : undefined;
};

/** Re-exported under the name this screen's components already use. */
export const formatDuplicateSender = formatPoolDepositDuplicateSender;
